import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShoppingCart, Zap, CreditCard, ShieldCheck, ArrowRight, ChevronDown, Play, Sparkles, BarChart3, Tablet, ArrowLeft, Smartphone, CheckCircle2, Settings } from 'lucide-react';
import './LukosPDVPremium.css';
import { LeadCaptureModal } from '../LeadCaptureModal/LeadCaptureModal';
import DiagonalHeroVideo from '../DiagonalHeroVideo/DiagonalHeroVideo';

interface AccordionItem {
  title: string;
  description: string;
}

const FeatureAccordion = ({ items }: { items: AccordionItem[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="lpp-accordion">
      {items.map((item, index) => (
        <div 
          key={index} 
          className={`lpp-acc-item ${activeIndex === index ? 'active' : ''}`}
          onClick={() => setActiveIndex(index)}
        >
          <div className="lpp-acc-header">
            <h3>{item.title}</h3>
            <ChevronDown size={20} className="lpp-acc-icon" />
          </div>
          <AnimatePresence>
            {activeIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="lpp-acc-body"
              >
                <p>{item.description}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export const LukosPDVHero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section className="lpp-section" id="lukos-pdv-premium">
      <LeadCaptureModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        productName="PDV e Frente de Caixa"
      />
      <div className="lpp-container">
        <div className="lpp-hero">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lpp-hero-content"
          >
            <div className="mb-8">
              <a href="/#solucoes-interativas" className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-slate-800 transition-colors">
                <ArrowLeft size={16} /> Voltar para visão geral
              </a>
            </div>
            <div className="lpp-brand-tag">
              <img src="/logo.png" alt="Lukos" className="w-4 h-4 object-contain" />
              <span>Lukos</span> 
              <span className="lpp-brand-pdv">PDV <Zap size={18} className="ml-1" fill="currentColor" /></span>
            </div>
            <h1 className="lpp-title">
              Performance e Agilidade <br />
              <span>no Ponto de Venda.</span>
            </h1>
            <p className="lpp-description">
              Aumente a eficiência operacional e elimine filas com o PDV mais ágil do mercado. Desenvolvido para a realidade intensa da pista e conveniência, garantindo transações instantâneas e integração total.
            </p>
            <div className="lpp-hero-actions">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="lpp-btn-primary shadow-xl"
              >
                Contratar serviço <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="lpp-hero-visual"
          >
            <div className="lpp-blob-container bg-black shadow-2xl border-4 border-white/10">
              <iframe 
                src="https://www.youtube.com/embed/diPZayPotMU?start=38"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full aspect-video"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const LukosPDVFeatures = () => {
  const features = [
    { 
      title: 'Venda Ágil na Pista', 
      description: 'Interface otimizada para a operação externa. Conclua qualquer venda em segundos, diretamente da ilha de abastecimento, com agilidade máxima para evitar filas.' 
    },
    { 
      title: 'Controle de Pista (Bicos)', 
      description: 'Monitore o status de cada bico em tempo real. Identifique bicos em abastecimento, livres ou com pendências diretamente na tela inicial.' 
    },
    { 
      title: 'Multimeios de Pagamento', 
      description: 'Integração nativa com Pix, cartões e frotas. Conciliação automática de cada transação sem necessidade de redigitação.' 
    }
  ];

  return (
    <>
    <section className="lpp-section">
      <div className="lpp-container">
        <div className="lpp-feature-grid lpp-feature-grid-reversed">
          <div className="lpp-feature-content">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-slate-500 font-medium mb-8 text-center md:text-left">
                Evolua sua operação, acompanhe dados e tome decisões <br /> estratégicas com a inteligência Lukos.
              </p>
              <FeatureAccordion items={features} />
              
              <button className="mt-10 flex items-center gap-3 px-8 py-4 bg-slate-50 text-slate-900 rounded-full font-bold text-sm hover:bg-slate-100 transition-all shadow-sm">
                <Play size={16} fill="currentColor" /> Ver vídeo
              </button>
            </motion.div>
          </div>

          <div className="lpp-mockup-display-wrapper">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, x: 30 }}
              whileInView={{ opacity: 1, scale: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lpp-mockup-outer-container shadow-[0_40px_100px_rgba(0,146,255,0.2)]"
            >
              <div className="lpp-mockup-inner-card">
                {/* Fake Dashboard Header */}
                <div className="flex items-center justify-between mb-8 border-b border-slate-50 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center p-1.5 shadow-lg shadow-purple-200">
                      <img src="/logo.png" alt="Lukos Logo" className="w-full h-full object-contain brightness-0 invert" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-[11px] tracking-tight uppercase">Monitor de Pista</h4>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm" />
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Live</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                   {[
                     { label: 'Bico 01', status: 'Abastecendo', color: 'green', val: 'R$ 145,20' },
                     { label: 'Bico 02', status: 'Livre', color: 'slate', val: 'Pronto' }
                   ].map((item, i) => (
                     <div key={i} className={`p-4 rounded-2xl border ${item.color === 'green' ? 'border-green-100 bg-green-50/30' : 'border-slate-100 bg-slate-50/50'} transition-all`}>
                        <div className="flex justify-between items-center mb-2">
                           <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{item.label}</span>
                           <Zap size={12} className={item.color === 'green' ? 'text-green-500' : 'text-slate-300'} fill={item.color === 'green' ? 'currentColor' : 'none'} />
                        </div>
                        <p className={`text-sm font-black ${item.color === 'green' ? 'text-slate-900' : 'text-slate-400'}`}>{item.val}</p>
                     </div>
                   ))}
                </div>

                <div className="bg-slate-900 rounded-2xl p-5 text-white shadow-2xl shadow-slate-900/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/20 blur-2xl rounded-full translate-x-1/2 -translate-y-1/2" />
                  <div className="relative z-10">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Venda Total Hoje</p>
                    <h3 className="text-2xl font-black tracking-tighter mb-4">R$ 12.845,00</h3>
                    <div className="flex items-center gap-2">
                       <div className="h-1 flex-1 bg-white/10 rounded-full overflow-hidden">
                          <motion.div 
                            initial={{ width: 0 }}
                            whileInView={{ width: '70%' }}
                            className="h-full bg-purple-500" 
                          />
                       </div>
                       <span className="text-[10px] font-bold text-purple-400">70% da Meta</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-slate-50 flex justify-between items-center">
                   <div className="flex -space-x-2">
                      {[1,2,3].map(i => (
                        <div key={i} className="w-6 h-6 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center">
                           <div className="w-full h-full rounded-full bg-slate-200" />
                        </div>
                      ))}
                   </div>
                   <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">3 Atendentes Online</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
    <DiagonalHeroVideo
      eyebrow="•  PDV MÓVEL  ·  SMARTPOS"
      title={<>A cobrança vai até<br />o carro. <span className="font-black">O cliente<br />não sai dele.</span></>}
      description="A maquininha vai na mão do frentista e fecha a venda ali, no bico. Com o Lukos Pay: cartão, carteira digital e criptomoeda — e o caixa deixa de ser o gargalo do horário de pico."
      mediaSrc="/Video/Modernizando_a_Gestão_de_Postos.mp4"
      poster="/lukos_pay_authentic_operation.png"
      ctaLabel="Ver o PDV por completo"
      ctaTo="/lukos-pdv"
    />
    </>
  );
};
